<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import apiService from '../services/api';
import PhoneInput from '../components/PhoneInput.vue';
import VerificationCodeInput from '../components/VerificationCodeInput.vue';

const router = useRouter();
const authStore = useAuthStore();

// Form steps
enum RegistrationStep {
  UserInfo = 1,
  EmailSent = 2,
  PhoneNumber = 3,
  PhoneVerification = 4,
  Complete = 5
}

const currentStep = ref(RegistrationStep.UserInfo);
const loading = ref(false);
const error = ref('');
const userId = ref<string | undefined>();


// Step 1: User Information
const name = ref('');
const email = ref('');
const password = ref('');
const confirmPassword = ref('');
const showPassword = ref(false);
const showConfirmPassword = ref(false);

// Step 2: Phone Number
const phoneNumber = ref('');
const phoneError = ref('');

// Step 3: Verification
const verificationError = ref('');
const verifying = ref(false);
const emailVerified = ref(false); // Track if email was verified during registration

// Computed
const canProceedUserInfo = computed(() => {
  return (
    name.value.trim().length > 0 &&
    email.value.trim().length > 0 &&
    password.value.length >= 8 &&
    password.value === confirmPassword.value
  );
});

const canProceedPhone = computed(() => {
  return phoneNumber.value.length >= 10; // At least country code + number
});

const passwordStrength = computed(() => {
  const pwd = password.value;
  if (!pwd) return { score: 0, label: '', color: '' };
  
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[^a-zA-Z0-9]/.test(pwd)) score++;
  
  const levels = [
    { score: 0, label: 'Too weak', color: 'bg-red-500' },
    { score: 1, label: 'Weak', color: 'bg-orange-500' },
    { score: 2, label: 'Fair', color: 'bg-yellow-500' },
    { score: 3, label: 'Good', color: 'bg-blue-500' },
    { score: 4, label: 'Strong', color: 'bg-green-500' },
    { score: 5, label: 'Very Strong', color: 'bg-green-600' },
  ];
  
  return levels[score];
});

// Step 1: User Info -> Register and Send Email Verification
async function proceedToRegister() {
  error.value = '';
  
  if (!canProceedUserInfo.value) return;
  
  loading.value = true;
  
  try {
    // Register user (this will send email verification)
    const response = await authStore.register(email.value, password.value, name.value);
    
    // Store userId for later phone verification
    if (response && response.user?.id) {
      userId.value = response.user.id;
    }
    
    currentStep.value = RegistrationStep.EmailSent;
  } catch (err: any) {
    error.value = err.response?.data?.error || err.message || 'Registration failed';
  } finally {
    loading.value = false;
  }
}

// Step 2: After email verification message -> Phone Number
function proceedToPhone() {
  currentStep.value = RegistrationStep.PhoneNumber;
}

// Step 2: Phone Number -> Send Verification Code
async function sendVerificationCode() {
  phoneError.value = '';
  
  if (!canProceedPhone.value) return;
  
  loading.value = true;
  
  try {
    // Sanitize phone number: remove all spaces and non-digit characters except +
    const sanitizedPhone = phoneNumber.value.replace(/[^\d+]/g, '');
    console.log('📱 Sending code to:', sanitizedPhone, '(original:', phoneNumber.value, ')');
    
    await apiService.sendPhoneVerificationCode(sanitizedPhone, 'sms');
    currentStep.value = RegistrationStep.PhoneVerification;
  } catch (err: any) {
    phoneError.value = err.response?.data?.error || 'Failed to send verification code';
  } finally {
    loading.value = false;
  }
}

// Step 3: Verify Code - Just verify phone, no registration
async function verifyPhone(code: string) {
  verificationError.value = '';
  verifying.value = true;
  
  try {
    // Sanitize phone number: remove all spaces and non-digit characters except +
    const sanitizedPhone = phoneNumber.value.replace(/[^\d+]/g, '');
    console.log('📱 Verifying phone:', sanitizedPhone);
    
    // Verify the phone number with userId
    const result = await apiService.verifyPhoneCode(sanitizedPhone, code, userId.value, email.value);
    
    // Check if email was also verified (user may have clicked link on another device)
    if (result && (result as any).user) {
      emailVerified.value = (result as any).user.emailVerified || false;
      console.log('📧 Email verification status after phone verification:', emailVerified.value);
    }
    
    // Show completion step
    currentStep.value = RegistrationStep.Complete;
  } catch (err: any) {
    verificationError.value = err.response?.data?.error || 'Verification failed';
  } finally {
    verifying.value = false;
  }
}

// Resend verification code
async function resendCode() {
  verificationError.value = '';
  
  try {
    await apiService.resendPhoneCode(phoneNumber.value, 'sms');
  } catch (err: any) {
    verificationError.value = err.response?.data?.error || 'Failed to resend code';
  }
}

// Go back
function goBack() {
  error.value = '';
  phoneError.value = '';
  verificationError.value = '';
  
  if (currentStep.value === RegistrationStep.PhoneNumber) {
    currentStep.value = RegistrationStep.EmailSent;
  } else if (currentStep.value === RegistrationStep.PhoneVerification) {
    currentStep.value = RegistrationStep.PhoneNumber;
  }
}

// Cancel and go to login
function goToLogin() {
  router.push('/login');
}
</script>

<template>
  <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full">
      <!-- Header -->
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Create Your Account
        </h1>
        <p class="text-gray-600 dark:text-gray-400">
          Join InvestBuddy.ai for AI-powered portfolio insights
        </p>
      </div>

      <!-- Progress Steps -->
      <div class="flex items-center justify-center mb-8">
        <div class="flex items-center space-x-2">
          <!-- Step 1 -->
          <div class="flex items-center">
            <div 
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all"
              :class="currentStep >= RegistrationStep.UserInfo ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'"
            >
              1
            </div>
            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300 hidden sm:inline">Info</span>
          </div>
          
          <div class="w-8 h-0.5 transition-all" :class="currentStep >= RegistrationStep.EmailSent ? 'bg-primary-600' : 'bg-gray-300'"></div>
          
          <!-- Step 2 -->
          <div class="flex items-center">
            <div 
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all"
              :class="currentStep >= RegistrationStep.EmailSent ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'"
            >
              2
            </div>
            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300 hidden sm:inline">Email</span>
          </div>
          
          <div class="w-8 h-0.5 transition-all" :class="currentStep >= RegistrationStep.PhoneNumber ? 'bg-primary-600' : 'bg-gray-300'"></div>
          
          <!-- Step 3 -->
          <div class="flex items-center">
            <div 
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all"
              :class="currentStep >= RegistrationStep.PhoneNumber ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'"
            >
              3
            </div>
            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300 hidden sm:inline">Phone</span>
          </div>
          
          <div class="w-8 h-0.5 transition-all" :class="currentStep >= RegistrationStep.PhoneVerification ? 'bg-primary-600' : 'bg-gray-300'"></div>
          
          <!-- Step 4 -->
          <div class="flex items-center">
            <div 
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all"
              :class="currentStep >= RegistrationStep.PhoneVerification ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'"
            >
              4
            </div>
            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300 hidden sm:inline">Verify</span>
          </div>
        </div>
      </div>

      <!-- Card -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
        <!-- Step 1: User Information -->
        <div v-if="currentStep === RegistrationStep.UserInfo" class="space-y-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Full Name
            </label>
            <input
              v-model="name"
              type="text"
              required
              placeholder="John Doe"
              class="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Address
            </label>
            <input
              v-model="email"
              type="email"
              required
              placeholder="john@example.com"
              class="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Password
            </label>
            <div class="relative">
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                required
                placeholder="••••••••"
                class="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <svg v-if="!showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              </button>
            </div>
            
            <!-- Password Strength Indicator -->
            <div v-if="password" class="mt-2">
              <div class="flex items-center gap-2">
                <div class="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    class="h-full transition-all duration-300" 
                    :class="passwordStrength.color"
                    :style="{ width: `${(passwordStrength.score / 5) * 100}%` }"
                  ></div>
                </div>
                <span class="text-xs font-medium" :class="passwordStrength.color.replace('bg-', 'text-')">
                  {{ passwordStrength.label }}
                </span>
              </div>
            </div>
            
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              At least 8 characters, with uppercase, lowercase, and numbers
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Confirm Password
            </label>
            <div class="relative">
              <input
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                required
                placeholder="••••••••"
                class="w-full px-4 py-2 bg-white dark:bg-gray-700 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                :class="confirmPassword && password !== confirmPassword ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'"
              />
              <button
                type="button"
                @click="showConfirmPassword = !showConfirmPassword"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <svg v-if="!showConfirmPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              </button>
            </div>
            <p v-if="confirmPassword && password !== confirmPassword" class="text-xs text-red-600 dark:text-red-400 mt-1">
              Passwords do not match
            </p>
          </div>

          <div v-if="error" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p class="text-sm text-red-600 dark:text-red-400">{{ error }}</p>
          </div>

          <button
            @click="proceedToRegister"
            :disabled="!canProceedUserInfo || loading"
            class="w-full px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {{ loading ? 'Processing...' : 'Continue' }}
          </button>

          <p class="text-center text-sm text-gray-600 dark:text-gray-400">
            Already have an account?
            <button @click="goToLogin" class="font-medium text-primary-600 hover:text-primary-700">
              Sign in
            </button>
          </p>
        </div>

        <!-- Step 2: Email Verification Sent -->
        <div v-else-if="currentStep === RegistrationStep.EmailSent" class="text-center space-y-6 py-8">
          <div class="inline-flex items-center justify-center w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full">
            <svg class="w-10 h-10 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          
          <div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Check Your Email
            </h3>
            <p class="text-gray-600 dark:text-gray-400 mb-4">
              We've sent a verification link to
            </p>
            <p class="text-lg font-medium text-gray-900 dark:text-white mb-6">
              {{ email }}
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Click the link in the email to verify your account.
            </p>
          </div>

          <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-left">
            <h4 class="font-semibold text-blue-900 dark:text-blue-100 mb-2">📧 Next Steps</h4>
            <ul class="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <li>1. Check your email inbox</li>
              <li>2. Click the verification link</li>
              <li>3. Complete phone verification below</li>
              <li>4. Sign in and start exploring!</li>
            </ul>
          </div>

          <div class="text-sm text-gray-500 dark:text-gray-400">
            <p>Didn't receive the email?</p>
            <p>Check your spam folder or <a href="#" class="text-primary-600 hover:text-primary-700">resend verification email</a></p>
          </div>

          <button
            @click="proceedToPhone"
            class="w-full px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Continue to Phone Verification
          </button>
        </div>

        <!-- Step 3: Phone Number -->
        <div v-else-if="currentStep === RegistrationStep.PhoneNumber" class="space-y-6">
          <div class="text-center mb-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Verify Your Phone Number
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              This helps us prevent fraud and keep your account secure
            </p>
          </div>

          <PhoneInput
            v-model="phoneNumber"
            :error="phoneError"
            :disabled="loading"
          />

          <div class="flex gap-3">
            <button
              @click="goBack"
              :disabled="loading"
              class="flex-1 px-4 py-3 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Back
            </button>
            
            <button
              @click="sendVerificationCode"
              :disabled="!canProceedPhone || loading"
              class="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {{ loading ? 'Sending...' : 'Send Code' }}
            </button>
          </div>
        </div>

        <!-- Step 3: Verification Code -->
                <!-- Step 3: Verification Code -->
                <!-- Step 4: Verification Code -->
        <div v-else-if="currentStep === RegistrationStep.PhoneVerification">
          <VerificationCodeInput
            :phone-number="phoneNumber"
            :loading="verifying"
            :error="verificationError"
            @verify="verifyPhone"
            @resend="resendCode"
            @cancel="goBack"
          />
        </div>

        <!-- Step 5: Complete -->
        <div v-else-if="currentStep === RegistrationStep.Complete" class="text-center space-y-6 py-8">
          <div class="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full">
            <svg class="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Registration Complete!
            </h3>
            <p class="text-gray-600 dark:text-gray-400 mb-4">
              {{ emailVerified ? 'Your account is ready! You can now sign in.' : 'Your phone number has been verified successfully' }}
            </p>
          </div>

          <!-- Show email verification warning only if email is NOT verified -->
          <div v-if="!emailVerified" class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-left">
            <div class="flex items-start gap-3">
              <svg class="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h4 class="font-semibold text-amber-900 dark:text-amber-100 mb-1">📧 One More Step</h4>
                <p class="text-sm text-amber-800 dark:text-amber-200">
                  Please verify your email address (<strong>{{ email }}</strong>) before you can sign in. Check your inbox for the verification link.
                </p>
              </div>
            </div>
          </div>
          
          <!-- Show success message if email IS verified -->
          <div v-else class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-left">
            <div class="flex items-start gap-3">
              <svg class="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <div>
                <h4 class="font-semibold text-green-900 dark:text-green-100 mb-1">✅ All Set!</h4>
                <p class="text-sm text-green-800 dark:text-green-200">
                  Both your email (<strong>{{ email }}</strong>) and phone number have been verified. You can now sign in to your account.
                </p>
              </div>
            </div>
          </div>

          <button
            @click="goToLogin"
            class="w-full px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            {{ emailVerified ? 'Sign In Now' : 'Go to Login' }}
          </button>
        </div>
      </div>

      <!-- Security Notice -->
      <div class="mt-6 text-center text-xs text-gray-500 dark:text-gray-400">
        <p>
          By creating an account, you agree to our 
          <a href="#" class="text-primary-600 hover:text-primary-700">Terms of Service</a> and 
          <a href="#" class="text-primary-600 hover:text-primary-700">Privacy Policy</a>
        </p>
      </div>
    </div>
  </div>
</template>
